import { ComponentProps, ReactNode } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import styled from 'styled-components';

import ConnectorNode from 'dashboard/apps/InquiryTemplateEditor/ConnectorNode';
import EditConnectorDialog from 'dashboard/apps/InquiryTemplateEditor/EditConnectorDialog/EditConnectorDialog';
import {
  ScreenDimensions,
  TemplateStepPreview,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { NodeWrapperAttributes } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/types';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import ScreenFlowNodeWrapper from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowNodeWrapper';
import { isInitialStep } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import Tooltip from 'dashboard/components/Tooltip';
import { InquiryTemplateVersionStep } from 'dashboard/models/inquiry-template-version';
import { Z_INDICES } from 'dashboard/styles/variables';
import useToggle from 'lib/hooks/useToggle';

// TODO (eric): replace with PDS popover when available
const EditConnectorTooltip = styled(Tooltip)`
  /* Chosen to make the tooltip align with the node at the default zoom level */
  margin: 8px;
  z-index: ${Z_INDICES.dropdown};
  border-radius: ${({ theme }) => theme.card.borderRadius} !important;
`;

export interface WrappedConnectorNodeProps
  extends Omit<
      ComponentProps<typeof ConnectorNode>,
      'previewPage' | 'stepPreview' | 'isShowingDialog'
    >,
    NodeWrapperAttributes {
  connectingStep: TemplateStepPreview;
  steps: InquiryTemplateVersionStep[];
  omitFromRun?: boolean;
  screenDimensions?: ScreenDimensions;
  nodeHighlighter?: ReactNode;
  handleConnectingStepChange: (stepName: string) => void;
  handleDeleteClick?: () => void;
  handleNodeClick?: () => void;
}

const WrappedConnectorNode = ({
  dataName,
  dataTest,
  dataMarketingId,
  title,
  steps,
  theme,
  connectingStep,
  omitFromRun,
  editable,
  selected,
  hasError,
  screenDimensions,
  nodeHighlighter,
  handleConnectingStepChange,
  handleDeleteClick,
  handleNodeClick,
}: WrappedConnectorNodeProps) => {
  const [
    showEditConnectorTooltip,
    { enable: enableEditConnectorTooltip, disable: disableEditConnectorTooltip },
  ] = useToggle(false);
  return (
    <EditConnectorTooltip
      disabled={!editable}
      placement='right-start'
      tooltipShown={showEditConnectorTooltip}
      hideArrow
      tooltip={
        editable && (
          <OutsideClickHandler onOutsideClick={disableEditConnectorTooltip}>
            <EditConnectorDialog
              theme={theme}
              selectedStep={connectingStep}
              stepOptions={steps
                .filter((step) => !isInitialStep(step))
                .map((step) => ({
                  title: getDisplayNameForStep(step),
                  name: step.name,
                }))}
              handleStepSelect={(stepName) => handleConnectingStepChange(stepName)}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              screenDimensions={screenDimensions}
            />
          </OutsideClickHandler>
        )
      }
    >
      <ScreenFlowNodeWrapper
        node={
          <ConnectorNode
            title={title}
            theme={theme}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            previewPage={connectingStep?.page}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            stepPreview={connectingStep?.stepPreview}
            isShowingDialog={showEditConnectorTooltip}
            editable={editable}
            selected={selected}
            hasError={hasError}
            screenDimensions={screenDimensions}
            nodeHighlighter={nodeHighlighter}
          />
        }
        title={title}
        data-name={dataName}
        data-test={dataTest}
        data-marketing-id={dataMarketingId}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        editable={editable}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        handleNodeClick={
          handleNodeClick != null
            ? () => {
                enableEditConnectorTooltip();
                handleNodeClick();
              }
            : null
        }
        handleEditClick={enableEditConnectorTooltip}
        handleDeleteClick={handleDeleteClick}
        withHoverControls={editable}
        omitFromRun={omitFromRun}
        withSticker
      />
    </EditConnectorTooltip>
  );
};

export default WrappedConnectorNode;
